import { tracking } from '@iwoca/frontend-tracking-library';

import { ExportRangePreset } from './components/ExportCustomers/exportDateRange';
import {
  CustomerRow,
  OrderingParam,
  TableSettingKey,
  TabPath,
} from './CustomersV2.types';
import { getAmplitudeDeviceCategory } from '../../utils/getAmplitudeDeviceCategory';

/**
 * Amplitude events for the Customers V2 page. The only place in the module that
 * names an event or assembles a property bag.
 *
 * Every function takes domain values and maps them to the wire values the
 * tracking plan expects. Two things depend on that indirection:
 *
 * - Property values must be back-end names, never front-end copy, so a reworded
 *   label can't rename an event property (`statusLabel.ts` already rewords two).
 * - `ordering`, `setting_name` and `source` are loose types in the plan
 *   (`string`/`any`), deliberately, so new sorts and settings need no plan
 *   revision. The narrow types live here instead, which is why each argument is
 *   a union rather than a string.
 */

/** API param names, not the internal `TableSettingKey` values. */
const SETTING_PARAM: Record<TableSettingKey, string> = {
  latestPayment: 'include_latest_payment',
  declined: 'include_declined',
};

const client_device = () => getAmplitudeDeviceCategory();

export const trackPageViewed = ({ tab }: { tab: TabPath }) =>
  tracking.ampli.customersV2PageViewed({
    tab_name: tab,
    client_device: client_device(),
  });

export const trackTabClicked = ({ from, to }: { from: TabPath; to: TabPath }) =>
  tracking.ampli.customersV2TabClicked({
    tab_name: to,
    source: from,
    client_device: client_device(),
  });

export const trackFilterClicked = ({
  tab,
  filterName,
  selected,
}: {
  tab: TabPath;
  /** Raw pill value, e.g. `business_details`, `offer_approved`. */
  filterName: string;
  /** `false` when an active pill is clicked to clear it. */
  selected: boolean;
}) =>
  tracking.ampli.customersV2FilterClicked({
    tab_name: tab,
    filter_name: filterName,
    filter_selected: selected,
    client_device: client_device(),
  });

export const trackSortChanged = ({
  tab,
  ordering,
  source,
}: {
  tab: TabPath;
  ordering: OrderingParam;
  source: 'header' | 'drawer';
}) =>
  tracking.ampli.customersV2SortChanged({
    tab_name: tab,
    ordering,
    source,
    client_device: client_device(),
  });

export const trackTableSettingToggled = ({
  tab,
  setting,
  enabled,
}: {
  tab: TabPath;
  setting: TableSettingKey;
  enabled: boolean;
}) =>
  tracking.ampli.customersV2TableSettingToggled({
    tab_name: tab,
    setting_name: SETTING_PARAM[setting],
    setting_enabled: enabled,
    client_device: client_device(),
  });

export const trackInfoModalOpened = ({ tab }: { tab: TabPath }) =>
  tracking.ampli.customersV2InfoModalOpened({
    tab_name: tab,
    client_device: client_device(),
  });

export const trackCustomerProfileOpened = ({
  tab,
  source,
  row,
}: {
  tab: TabPath;
  source: 'table' | 'search' | 'url';
  row: CustomerRow;
}) =>
  tracking.ampli.customersV2CustomerProfileOpened({
    tab_name: tab,
    source,
    spending_limit_status: row.spending_limit_status.value,
    application_stage: row.application_stage.value,
    client_device: client_device(),
  });

export const trackCopyEmailClicked = () =>
  tracking.ampli.customersV2CopyEmailClicked({
    client_device: client_device(),
  });

export const trackSearchPerformed = ({
  tab,
  event,
}: {
  tab: TabPath;
  event: 'term_entered' | 'result_clicked' | 'declined_opened';
}) =>
  tracking.ampli.customersV2SearchPerformed({
    tab_name: tab,
    customers_search_event: event,
    client_device: client_device(),
  });

export const trackExportClicked = ({
  tab,
  filterName,
  includeDeclined,
  dateRange,
  acrossBothTabs,
}: {
  /** The tab the export was started from, union export or not. */
  tab: TabPath;
  /**
   * Absent when the tab is unfiltered — the common case for an export — or
   * when exporting across both tabs, where no pill filter applies.
   */
  filterName: string | null;
  includeDeclined: boolean;
  /** Preset key, not the resolved bounds: dates are unbounded cardinality. */
  dateRange: ExportRangePreset;
  acrossBothTabs: boolean;
}) =>
  tracking.ampli.customersV2ExportClicked({
    tab_name: tab,
    ...(filterName && { filter_name: filterName }),
    include_declined: includeDeclined,
    export_date_range: dateRange,
    export_across_tabs: acrossBothTabs,
    client_device: client_device(),
  });
